/**
 * TierServiceMatrixPage — `/settings/tier-service-matrix`.
 *
 * Live read from `GET /v1/franchise/tiers` (Phase 4a). Per-(tier, service)
 * operator overrides are persisted via the generic settings-collection store
 * on service-audit (`/v1/settings-collections/tier_service_overrides`). Each
 * override is a tuple `{ tierCode, serviceCode, allowed }` — the merged view
 * is "override wins over base mapping when present".
 */
import { type JSX, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { listTiers } from '../../lib/api/franchise';
import {
  getSettingsCollection,
  putSettingsCollection,
} from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

interface TierOverride {
  readonly tierCode: string;
  readonly serviceCode: string;
  readonly allowed: boolean;
}

const COLLECTION_KEY = 'tier_service_overrides' as const;

export function TierServiceMatrixPage(): JSX.Element {
  const qc = useQueryClient();

  const tiers = useQuery({
    queryKey: ['franchise', 'tiers', 'matrix'],
    queryFn: () => listTiers({ active: true }),
    retry: false,
    staleTime: 60_000,
  });

  const overridesQuery = useQuery({
    queryKey: ['settings-collection', COLLECTION_KEY],
    queryFn: () => getSettingsCollection<TierOverride>(COLLECTION_KEY),
    retry: false,
    staleTime: 30_000,
  });

  const [overrides, setOverrides] = useState<readonly TierOverride[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (overridesQuery.data && !dirty) {
      setOverrides(overridesQuery.data.items);
    }
  }, [overridesQuery.data, dirty]);

  const saveMut = useMutation({
    mutationFn: (next: readonly TierOverride[]) =>
      putSettingsCollection<TierOverride>(COLLECTION_KEY, next),
    onSuccess: async () => {
      setDirty(false);
      await qc.invalidateQueries({ queryKey: ['settings-collection', COLLECTION_KEY] });
    },
  });

  const denied =
    tiers.isError && tiers.error instanceof ApiError && (tiers.error.status === 401 || tiers.error.status === 403);

  // Build the union of all services across tiers so the matrix has stable rows.
  const services = (() => {
    const set = new Set<string>();
    tiers.data?.items.forEach((t) => t.allowedServices.forEach((s) => set.add(s)));
    overrides.forEach((o) => set.add(o.serviceCode));
    return Array.from(set).sort();
  })();

  function isAllowed(tierCode: string, serviceCode: string, baseAllowed: boolean): boolean {
    const ov = overrides.find(
      (o) => o.tierCode === tierCode && o.serviceCode === serviceCode,
    );
    if (ov) return ov.allowed;
    return baseAllowed;
  }

  function toggle(tierCode: string, serviceCode: string, baseAllowed: boolean): void {
    const idx = overrides.findIndex(
      (o) => o.tierCode === tierCode && o.serviceCode === serviceCode,
    );
    const currentlyAllowed = isAllowed(tierCode, serviceCode, baseAllowed);
    const desired = !currentlyAllowed;
    let next: readonly TierOverride[];
    if (idx === -1) {
      next = [...overrides, { tierCode, serviceCode, allowed: desired }];
    } else if (desired === baseAllowed) {
      // Override now matches base — drop the override entry.
      next = overrides.filter((_, i) => i !== idx);
    } else {
      next = overrides.map((o, i) => (i === idx ? { ...o, allowed: desired } : o));
    }
    setOverrides(next);
    setDirty(true);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tier service matrix"
        description="Which services are available at TIER_1 / TIER_2 / TIER_3."
        actions={
          dirty ? (
            <button
              type="button"
              onClick={() => saveMut.mutate(overrides)}
              disabled={saveMut.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium disabled:opacity-60"
            >
              <Save className="w-4 h-4" /> {saveMut.isPending ? 'Saving…' : 'Save overrides'}
            </button>
          ) : null
        }
      />

      <p className="text-xs text-muted -mt-3">
        Click a cell to toggle the service for that tier. Save when done.
      </p>

      {saveMut.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Save failed: {(saveMut.error as Error).message}
        </div>
      ) : null}

      {tiers.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          The dev token does not carry <code className="font-mono">Franchise/read</code>.
        </div>
      ) : tiers.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Service unreachable: {(tiers.error as Error).message}
        </div>
      ) : services.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No services defined.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-border">
              <tr>
                <th className="px-4 py-2 text-left text-[10px] font-semibold text-muted uppercase">
                  Service
                </th>
                {tiers.data?.items.map((t) => (
                  <th
                    key={t.code}
                    className="px-4 py-2 text-center text-[10px] font-semibold text-muted uppercase"
                  >
                    {t.displayName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {services.map((svc) => (
                <tr key={svc} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs font-mono text-gray-900">{svc}</td>
                  {tiers.data?.items.map((t) => {
                    const baseAllowed = t.allowedServices.includes(svc);
                    const allowed = isAllowed(t.code, svc, baseAllowed);
                    return (
                      <td
                        key={t.code}
                        onClick={() => toggle(t.code, svc, baseAllowed)}
                        className={`px-4 py-2 text-center cursor-pointer transition-colors ${
                          allowed
                            ? 'bg-nipost-green-light hover:bg-nipost-green/20'
                            : 'hover:bg-amber-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={allowed}
                          aria-label={`${svc} at ${t.code}`}
                          onChange={() => toggle(t.code, svc, baseAllowed)}
                          onClick={(e) => e.stopPropagation()}
                          className="cursor-pointer"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
